import { useContext } from 'react'

import { toast } from 'react-toastify'

import { useLatestEntity, useThunkDispatch } from '@domain-group/react-hooks'

import { encodeParams, generateApplicationUrl, launchModal } from '@domain-group/javascript-utils'

import useSelector from '@hooks/useSelector'

import { baseURL } from '@functions/api'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { PublicationOrderModel, PublicationOrderRequestOptions } from '@models/types'

import type { EntityOptions } from '@redux/modules/module'

import * as publicationOrderActions from '@redux/modules/publicationOrder'
import type { AppDispatch } from '@redux/store'

import PageContext from '@contexts/pageContext'

export const generateUrls = (publicationOrder: PublicationOrderModel) => {
  const { id } = publicationOrder || {}

  // @ts-expect-error TS2339
  const { applicationUrl } = generateApplicationUrl({
    applicationKey: 'realhub_legacy',
    // @ts-expect-error TS2322
    environment: process.env.REACT_APP_APPLICATION_ENV,
  })

  return {
    billingCsv: `${baseURL}/publication_orders/${id}/billing_csv.json`,
    bookingForm: `${baseURL}/publication_orders/${id}/booking_form.json`,
    newscorpXml: `${baseURL}/publication_orders/${id}/newscorp_xml_export`,
    publicUrl: `${applicationUrl}/admin/public_pages#/publication-orders/${id}`,
  }
}

type CreatePublicationOrderParams = {
  dispatch: AppDispatch,
  publicationOrderParams: Partial<PublicationOrderModel>,
  requestOptions?: PublicationOrderRequestOptions,
}

const createPublicationOrder = (params: CreatePublicationOrderParams) => {
  const { dispatch, publicationOrderParams, requestOptions } = params
  const { createPublicationOrder: createFn } = publicationOrderActions

  // @ts-expect-error TS2345
  return dispatch(createFn(publicationOrderParams, requestOptions))
}

type AutoPaginateParams = {
  callbacks: { [key:string]: (payload: HandleCallbackActionParams) => void },
  dispatch: AppDispatch,
  publicationEditionId: number,
  requestOptions?: PublicationOrderRequestOptions,
}

const autoPaginate = (params: AutoPaginateParams) => {
  const {
    callbacks, dispatch, publicationEditionId, requestOptions,
  } = params

  const {
    selectPublicationBookingId,
  } = callbacks

  const { autoPaginate: autoPaginateFn } = publicationOrderActions

  return dispatch(autoPaginateFn(publicationEditionId, requestOptions)).then((response) => {
    // @ts-expect-error TS2339
    const { data, success } = response

    // @ts-expect-error TS2339
    if (success && !!data?.bookings.length){
      // @ts-expect-error TS2339
      const { bookings, publication_edition_id, publication_id } = data

      const entityParams = {
        id: bookings[0].id,
        publication_edition_id,
        publication_id,
      }

      // @ts-expect-error TS2345
      selectPublicationBookingId({ entityParams })

      return response
    }

    return response
  })
}

type RemovedBookingItemsParams = {
  dispatch: AppDispatch,
  publicationOrderId: number,
  requestOptions?: EntityOptions,
}

const removedBookingItems = (params: RemovedBookingItemsParams) => {
  const { dispatch, publicationOrderId, requestOptions } = params

  const { removedBookingItems: removedFn } = publicationOrderActions

  return dispatch(removedFn(publicationOrderId, requestOptions))
}

type PublicationOrderParams = {
  dispatch: AppDispatch,
  publicationOrder: PublicationOrderModel,
  revertParams?: { ticket_number?: string },
  requestOptions?: EntityOptions,
}

const revertPublicationOrder = (params: PublicationOrderParams) => {
  const {
    publicationOrder, revertParams, requestOptions, dispatch,
  } = params

  const { revertPublicationOrder: revertFn } = publicationOrderActions

  // @ts-expect-error TS2345
  return dispatch(revertFn(publicationOrder, revertParams, requestOptions))
}

const createPublicToken = (params: PublicationOrderParams) => {
  const { publicationOrder, dispatch } = params
  const { createPublicToken: createTokenFn } = publicationOrderActions

  return dispatch(createTokenFn(publicationOrder))
}

const sendAgencyArtworkAlert = (params: PublicationOrderParams) => {
  const { publicationOrder, dispatch } = params
  const { sendAgencyArtworkAlert: sendArtFn } = publicationOrderActions

  return dispatch(sendArtFn(publicationOrder))
}

const sendAgencyBookingAlert = (params: PublicationOrderParams) => {
  const { publicationOrder, dispatch } = params
  const { sendAgencyBookingAlert: sendBookFn } = publicationOrderActions

  return dispatch(sendBookFn(publicationOrder))
}

type OpenPublicPageUrl = {
  publicationBookingId: number,
  publicationOrder: PublicationOrderModel,
  publicToken: string,
}

const openPublicPageUrl = (params: OpenPublicPageUrl) => {
  const { publicationOrder, publicToken, publicationBookingId } = params

  const encodedParams = encodeParams({ public_token: publicToken, booking_id: publicationBookingId })
  const { publicUrl } = generateUrls(publicationOrder)

  window.open(`${publicUrl}?${encodedParams}`)
}

type GeneratePublicTokenParams = {
  dispatch: AppDispatch,
  publicationOrder: PublicationOrderModel,
  publicationBookingId: number,
}

const generatePublicToken = (params: GeneratePublicTokenParams) => {
  const { dispatch, publicationOrder, publicationBookingId } = params
  const { public_token: publicToken } = publicationOrder

  if (publicToken){
    openPublicPageUrl({ publicationOrder, publicToken, publicationBookingId })
  } else {
    createPublicToken({ publicationOrder, dispatch }).then((result) => {
      const { success } = result

      if (!success) toast(result.errors[0])

      if (success){
        const { public_token: createdToken } = result.data

        openPublicPageUrl({ publicationOrder, publicToken: createdToken, publicationBookingId })
      }

      return result
    })
  }
}
export type BulkReleasePublicationBookingsParams = {
  dispatch: AppDispatch,
  publicationOrder: PublicationOrderModel,
  requestOptions?: PublicationOrderRequestOptions,
}

const bulkReleasePublicationBookings = (params: BulkReleasePublicationBookingsParams) => {
  const { dispatch, publicationOrder, requestOptions } = params

  const { bulkReleasePublicationBookings: bulkReleaseFn } = publicationOrderActions

  return dispatch(bulkReleaseFn(publicationOrder, requestOptions))
}

function usePublicationOrder(initEntity: Partial<PublicationOrderModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: publicationOrder }:
  { entity: PublicationOrderModel} = useLatestEntity(initEntity, 'publicationOrders')

  const dispatch = useThunkDispatch()

  const { creating, loading, updating } = useSelector(reduxState => reduxState.publicationOrders)

  const { callbacks } = useContext(PageContext)

  return {
    callbacks: {
      autoPaginate: (payload: Partial<AutoPaginateParams>) => (
        // @ts-expect-error TS2345
        autoPaginate({ ...payload, dispatch })
      ),
      createPublicationOrder: (
        publicationOrderParams: Partial<PublicationOrderModel>,
        entityOptions?: PublicationOrderRequestOptions,
      ) => (
        createPublicationOrder({ publicationOrderParams, dispatch, requestOptions: entityOptions })
      ),
      bulkReleasePublicationBookings: () => (
        bulkReleasePublicationBookings({
          dispatch,
          publicationOrder,
          requestOptions: { include_publication_order_bookings: true },
        })
      ),
      generatePublicToken: (publicationBookingId: number) => (
        generatePublicToken({ publicationBookingId, publicationOrder, dispatch })
      ),
      removedBookingItems: (payload: Partial<RemovedBookingItemsParams>) => (
        // @ts-expect-error TS2345
        removedBookingItems({ ...payload, dispatch })
      ),
      revertPublicationOrder: (revertParams: { ticket_number: string }, requestOptions?: EntityOptions) => (
        revertPublicationOrder({
          publicationOrder, revertParams, requestOptions, dispatch,
        })
      ),
      revertPublicationOrderModal: () => launchModal({
        callbacks,
        modalKey: 'RevertPublicationOrderModal',
        payload: { publicationOrder },
      }),
      sendAgencyArtworkAlert: () => (
        sendAgencyArtworkAlert({ publicationOrder, dispatch })
      ),
      sendAgencyBookingAlert: () => (
        sendAgencyBookingAlert({ publicationOrder, dispatch })
      ),
    },
    creating,
    loading,
    publicationOrder,
    updating,
    urls: generateUrls(publicationOrder),
  }
}

export type UsePublicationOrderPayload = ReturnType<typeof usePublicationOrder>

export default usePublicationOrder
